/**
 * Server Event Data
 * 
 * Configuration that maps server-sent event types to client-side actions.
 * 
 * Action types:
 *   - 'notify': Show a toast notification
 *   - 'refresh': Invalidate React Query cache for the specified query keys
 *   - 'nSerie':  Move a number series on in the cached settings and open forms (nSerieManager)
 *   - 'model':   Refetch the org's model in the background; it applies on the next navigation (loadModel)
 *
 * exactUserId:
 *   - true:  Only execute this action for the user who triggered the operation
 *   - false: Execute this action for all users in the organization
 */

const serverEventData = {

    /*
     * The bulk "update selected records" action, from any list that offers it
     * (35.01 M5 gave them their own list actions). One pair of entries covers
     * every entity: which list was touched is in the payload, not in this
     * declaration.
     *
     * The counts are the report. A batch is a loop of independent writes rather
     * than one transaction, so rows can be refused while others land — which is
     * why the partial case is its own event and its own colour. Nothing else on
     * screen says how many records were written.
     */
    'batch.update': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.batch.update.title',
                    message: 'event.batch.update.message',
                    // Payload fields, in the order the message's placeholders use them.
                    values: ['successful']
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParamsFromPayload: 'entity'
            }
        ]
    },

    'batch.updatePartial': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'warning',
                    title: 'event.batch.updatePartial.title',
                    message: 'event.batch.updatePartial.message',
                    values: ['successful', 'total']
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParamsFromPayload: 'entity'
            }
        ]
    },

    'creditNote.match': {
        event: 'creditNote.match',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.creditNote.match.title',
                    message: 'event.creditNote.match.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['creditNote', 'invoice']
            }
        ]
    },

    'creditNote.unmatch': {
        event: 'creditNote.unmatch',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.creditNote.unmatch.title',
                    message: 'event.creditNote.unmatch.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['creditNote', 'invoice']
            }
        ]
    },

    /*
     * Background OCR of an uploaded file finished (task 38.02 §5), or failed
     * for good — `payload.status` says which. No toast: a receipt being read
     * is not news. Any query keyed by the file id (the OCR of that file) is
     * refetched.
     */
    'file.ocr': {
        actions: [
            {
                type: 'refresh',
                exactUserId: false,
                queryParamsFromPayload: 'fileId'
            },
            // Purchases › Inbox shows how far each document has been read (38.04).
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['inboxDocument']
            }
        ]
    },

    /*
     * A file was read into a suggested expense (task 38.03 §3), or failed to
     * be. Refetches any query keyed by the file id; the suggestion panel
     * listens for this.
     */
    'file.extract': {
        actions: [
            {
                type: 'refresh',
                exactUserId: false,
                queryParamsFromPayload: 'fileId'
            },
            // Purchases › Inbox shows how far each document has been read (38.04).
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['inboxDocument']
            }
        ]
    },

    /*
     * A suggested expense was classified — supplier and categories (task
     * 38.03 §4) — or failed to be. The suggestion panel refetches by file id.
     */
    'file.classify': {
        actions: [
            {
                type: 'refresh',
                exactUserId: false,
                queryParamsFromPayload: 'fileId'
            }
        ]
    },

    /*
     * The queued half of import apply (35.02 M4). One entry covers every
     * importable list: the entity is in the payload, not in this declaration,
     * which is what keeps import free of per-entity client code.
     */
    // An inbox document moved between To do and Used by an expense's save or
    // delete (38.04) — done in the background, after the save returned.
    'inboxDocument.changed': {
        actions: [
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['inboxDocument']
            }
        ]
    },
    'import.apply': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.import.apply.title',
                    message: 'event.import.apply.message',
                    // Payload fields, in the order the message's placeholders use them.
                    values: ['applied', 'conflicts', 'failed']
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                // The list's own address, not the entity name: a subcollection is
                // cached under its full path (`paymentAccount/ACC1/paymentTransaction`),
                // and invalidating by entity would leave it showing stale rows.
                queryParamsFromPayload: 'endpoint'
            }
        ]
    },

    'invoicePayment.match': {
        event: 'invoicePayment.match',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.invoicePayment.match.title',
                    message: 'event.invoicePayment.match.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['invoicePayment']
            }
        ]
    },

    'invoicePayment.unmatch': {
        event: 'invoicePayment.unmatch',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.invoicePayment.unmatch.title',
                    message: 'event.invoicePayment.unmatch.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['invoicePayment']
            }
        ]
    },

    'expense.match': {
        event: 'expense.match',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.expense.match.title',
                    message: 'event.expense.match.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['expense']
            }
        ]
    },

    'expense.unmatch': {
        event: 'expense.unmatch',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.expense.unmatch.title',
                    message: 'event.expense.unmatch.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['expense']
            }
        ]
    },

    /*
     * The org's model was deployed, rolled back or reset (30.03.05 §6). Every
     * open client fetches the new model in the background and swaps it in on
     * its next navigation, never mid-form (30.03.06 §6). A deploy that changed
     * only the server or logic namespaces is ignored: nothing the client
     * renders moved.
     */
    'model.deploy': {
        actions: [
            { type: 'model', exactUserId: false }
        ]
    },

    /*
     * A booking took a number from a series (37.03). Every open client — not
     * only the one that booked — moves the module's cached settings on to the
     * next number, so a new document is offered it, and a new document already
     * open that still shows the number just taken is given the next one. It is
     * a freshness hint: booking itself refuses a number the series log has.
     */
    'nserie.advanced': {
        actions: [
            {
                type: 'nSerie',
                exactUserId: false
            }
        ]
    },

    /*
     * A new payment account is given the GL account its postings land on
     * automatically (linkBankGlAccount). Claiming one that the chart already
     * had is not announced — the field on the card shows it, and nothing was
     * created. Minting is: it writes a row into a statutory chart, and the user
     * should hear which account that was and what it was for.
     */
    'paymentAccount.glAccountMinted': {
        event: 'paymentAccount.glAccountMinted',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.paymentAccount.glAccountMinted.title',
                    message: 'event.paymentAccount.glAccountMinted.message',
                    values: ['number', 'accountName']
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['paymentAccount', 'glAccount']
            }
        ]
    },

    /*
     * Two payment accounts on one GL account is legal — a DKK and a EUR account
     * at the same bank are sometimes kept as one ledger account — but it makes
     * a reconciliation ambiguous. Said out loud, not prevented.
     */
    'paymentAccount.glAccountShared': {
        event: 'paymentAccount.glAccountShared',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'warning',
                    title: 'event.paymentAccount.glAccountShared.title',
                    message: 'event.paymentAccount.glAccountShared.message',
                    values: ['glAccountName', 'other']
                }
            }
        ]
    },

    'priceList.updateItems': {
        event: 'priceList.updateItems',
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.priceList.updateItems.title',
                    message: 'event.priceList.updateItems.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['priceList']
            }
        ]
    },

    'workCenter.createRack': {
        event: 'workCenter.createRack',
        // No 'notify' here — the triggering user already gets a success toast from
        // postActionFn; this refresh keeps the form (and other users' lists) current.
        actions: [
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['workCenter', 'rack']
            }
        ]
    },

    'adminSettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['adminSettings'] }]
    },
    'salesSettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['salesSettings'] }]
    },
    'inventorySettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['inventorySettings'] }]
    },
    'purchasesSettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['purchasesSettings'] }]
    },
    'projectSettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['projectSettings'] }]
    },
    'homeSettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['homeSettings'] }]
    },
    'crmSettings.updated': {
        actions: [{ type: 'refresh', exactUserId: false, queryParams: ['crmSettings'] }]
    },

    'productFamily.generateVariants': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.productFamily.generateVariants.title',
                    message: 'event.productFamily.generateVariants.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['product']
            }
        ]
    },

    'purchaseRequest.generatePurchaseOrders': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.purchaseRequest.generatePurchaseOrders.title',
                    message: 'event.purchaseRequest.generatePurchaseOrders.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['purchaseRequest', 'purchaseOrder']
            }
        ]
    },

    'bomVersion.activate': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.bomVersion.activate.title',
                    message: 'event.bomVersion.activate.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['product', 'bomVersion']
            }
        ]
    },

    'routingVersion.activate': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.routingVersion.activate.title',
                    message: 'event.routingVersion.activate.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['product', 'routingVersion']
            }
        ]
    },

    'purchaseRequest.sourceLines': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.purchaseRequest.sourceLines.title',
                    message: 'event.purchaseRequest.sourceLines.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['purchaseRequest']
            }
        ]
    },

    'workOrder.book': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.workOrder.book.title',
                    message: 'event.workOrder.book.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['workOrder', 'workOperation']
            }
        ]
    },

    'workOrder.createOperations': {
        actions: [
            {
                type: 'notify',
                exactUserId: true,
                notification: {
                    type: 'success',
                    title: 'event.workOrder.createOperations.title',
                    message: 'event.workOrder.createOperations.message'
                }
            },
            {
                type: 'refresh',
                exactUserId: false,
                queryParams: ['workOrder', 'workOperation']
            }
        ]
    }

}

export default serverEventData
